import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Sparkles, LayoutTemplate, Loader2 } from 'lucide-react';

export default function IvrCreateModal({ api, subAccountId, mode, onClose, onCreated }) {
  const [tab, setTab] = useState(mode || 'template');
  const [templates, setTemplates] = useState([]);
  const [pick, setPick] = useState('');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/api/call-routing/templates').then(r => { setTemplates(r.data); setPick(r.data[0]?.id || ''); }).catch(() => {}); }, [api]);

  const go = async () => {
    setBusy(true);
    try {
      const r = tab === 'template'
        ? await api.post(`/api/sub-accounts/${subAccountId}/call-routing/from-template`, { template_id: pick })
        : await api.post(`/api/sub-accounts/${subAccountId}/call-routing/ai-build`, { prompt });
      toast.success(tab === 'template' ? 'Flow created from template' : 'AI Architect built your IVR agent and flow');
      onCreated(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not create the flow');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ivr-create-modal">
      <div className="w-full max-w-xl rounded-2xl bg-[#0f1115] border border-white/10 p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">New IVR / call routing flow</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="ivr-create-close"><X size={18} /></button>
        </div>
        <div className="inline-flex gap-1 p-1 rounded-xl bg-white/5 mb-4">
          {[['template', 'Start from a template', LayoutTemplate], ['ai', 'Build with AI Architect', Sparkles]].map(([id, l, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`ivr-create-tab-${id}`}
              className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 ${tab === id ? 'bg-white/10 text-white' : 'text-gray-400'}`}><Icon size={14} />{l}</button>))}
        </div>
        {tab === 'template' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {templates.map(t => (
              <button key={t.id} onClick={() => setPick(t.id)} data-testid={`ivr-template-${t.id}`}
                className={`text-left p-3 rounded-xl border transition-colors ${pick === t.id ? 'border-teal-400/60 bg-teal-400/10' : 'border-white/10 hover:border-white/20'}`}>
                <div className="text-sm font-semibold">{t.name}</div>
                <div className="text-[11px] text-gray-400 mt-0.5">{t.options.join(' · ')}</div>
              </button>))}
            <p className="sm:col-span-2 text-[11px] text-gray-400">Creates the menu, departments with hold queues, and AI fallbacks. Then pick your IVR agent on Start and add department numbers.</p>
          </div>
        ) : (
          <div>
            <textarea rows={7} value={prompt} onChange={e => setPrompt(e.target.value)} data-testid="ivr-ai-prompt"
              placeholder={'e.g. Dental clinic "Bright Smiles". Options: 1 appointments (front desk +15551110001 and +15551110002, ring together, Mon–Fri 8–5 ET), 2 billing (+15551110003), 3 emergencies go to the AI agent. Max 5 AI calls, callback offer after 5 minutes, closed Dec 25 and Jan 1.'}
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-teal-400/50 resize-none" />
            <p className="text-[11px] text-gray-400 mt-2">Creates an IVR agent (prompt, greeting, Call Routing tool) and the full flow: menu, departments, queues, hours, holidays and failsafe. Both are drafts for you to review. $0.50 per build.</p>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-white/5">Cancel</button>
          <button onClick={go} disabled={busy || (tab === 'ai' ? prompt.trim().length < 20 : !pick)} data-testid="ivr-create-submit"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-teal-400 text-[#08090d] hover:bg-teal-500 disabled:opacity-50 flex items-center gap-1.5">
            {busy && <Loader2 size={14} className="animate-spin" />}{tab === 'ai' ? (busy ? 'Building…' : 'Build IVR') : 'Create flow'}
          </button>
        </div>
      </div>
    </div>
  );
}
